// <purpose>THE lifecycle bucket vocabulary: one type whose values are simultaneously the tab key,
// the served `bucket` face key (useCategoryMeta().face('bucket', key)) and the API's own `bucket`
// wire param. Before this file, the same six-value set was declared three times — distributionStats.ts's
// `LifecycleBucket`, useArchivedPagination.ts's `ArchiveBucket`, DashboardSeite.tsx's `VerlaufBucket`
// — and one of the three (`ArchiveBucket`) spelled its third member differently from the wire
// ('unklassifiziert' vs the API's 'ungeklaert'), paying a translation (`toApiBucket`) for the
// privilege. ONE spelling here means the tab a clerk clicks, the request the FE sends and the word
// the tenant serves for it can never quietly disagree (Task R2, part 4c, 2026-09-27).</purpose>
// <constraints>Every bucket-typed variable in the app imports from here — never redeclare the union
// or a subset of it locally. The values ARE core's TicketBucket names, lowercased.</constraints>

import { isSettledStatus } from './settledRow';

/** The lifecycle buckets = core's TicketBucket names lowercased = the served `bucket` face keys = the
 *  API's bucket param. ONE spelling, so the tab key, the request and the word lookup can never disagree. */
export type LifecycleBucket = 'uploads' | 'offen' | 'entschieden' | 'erledigt' | 'liegengeblieben' | 'ungeklaert';

// Runtime twin of the type above, declared beside it so a fixture-vs-type comparison (see
// lifecycleBuckets.test.ts) can hold TypeScript's own union to account without hand-copying it.
export const LIFECYCLE_BUCKETS: readonly LifecycleBucket[] = ['uploads', 'offen', 'entschieden', 'erledigt', 'liegengeblieben', 'ungeklaert'];

export type ActiveBucket = Extract<LifecycleBucket, 'uploads' | 'offen' | 'entschieden'>;
export type ArchiveBucket = Extract<LifecycleBucket, 'erledigt' | 'liegengeblieben' | 'ungeklaert'>;

export const ARCHIVE_BUCKETS: readonly ArchiveBucket[] = ['erledigt', 'liegengeblieben', 'ungeklaert'];

// The bucket tab shimmer's width, per key (fix wave 2026-09-27, item 6) — AntwortenSeite,
// DashboardSeite's Verlauf tabs and NavBar's Uploads item all used one flat 64px shimmer for every
// ActiveBucket key, so a tenant on the seed words saw "Offen" (5 chars) and "Uploads" (7 chars)
// visibly narrower than their reserved box, then jump to their real, tighter width the instant
// /config landed — the same first-paint mismatch ArchivSeite's own ARCHIVE_TAB_SHIMMER_WIDTH (below
// it in ArchivSeite.tsx) already fixed for its three keys. ONE shared table here, imported by all
// three consumers, so the three surfaces reading the SAME ActiveBucket keys can never disagree on
// the shimmer they show for one. Same basis as ArchivSeite's own table (~7.1px/char for this
// font/weight/size) — sizes a PLACEHOLDER only, spells no word itself:
//   "Uploads" (7 chars)   -> 50
//   "Offen" (5 chars)     -> 36
//   "Entschieden" (11 chars) -> 78
export const ACTIVE_TAB_SHIMMER_WIDTH: Record<ActiveBucket, number> = {
  uploads: 50,
  offen: 36,
  entschieden: 78,
};

// A stored/URL bucket value from before this part ('unklassifiziert') is simply not one of these —
// no translation arm for it (no-deprecation-ceremonies): it falls through to the caller's own
// default exactly like any other unrecognised value would.
export const isArchiveBucket = (v: unknown): v is ArchiveBucket =>
  typeof v === 'string' && (ARCHIVE_BUCKETS as readonly string[]).includes(v);

/** The tab a restored ticket returns to: its previous pipeline status decides. A SETTLED row —
 *  approved, sent, or declined (Genehmigen settles the row, 2026-10-23: Wiederherstellen undoes the
 *  filing, never the decision) — returns to Entschieden; everything else to Offen. */
export const bucketForRestoredStatus = (previousStatus?: string | null): 'offen' | 'entschieden' =>
  isSettledStatus(previousStatus) ? 'entschieden' : 'offen';

/** The restore toast text (fix round 1, Review Focus / raw-key leak). `bucketWord` is the CALLER's
 *  already-resolved word — `undefined` when the caller decided the vocabulary isn't trustworthy yet
 *  (cm.configLoading), same convention as claimStateLabel's caller-supplied `face`. Never receives
 *  a raw key: the caller omits the clause instead of passing one, exactly like every native `title=`
 *  tooltip elsewhere in this task. */
export const restoredTicketToast = (bucketWord: string | undefined): string =>
  bucketWord ? `Ticket wiederhergestellt — Reiter: ${bucketWord}` : 'Ticket wiederhergestellt';
